<template>
  <button
    class="custom-button primary-btn flex gap-1 items-center w-full justify-center"
    :aria-busy="adding ? 'true' : null"
    @click="handleAddToCart"
    :disabled="!product?.stock"
  >
    {{ $t("elements.store.product.buttonAddToCart") }}
    <!-- The icon slot keeps its size while a request is pending, so the label
         never moves. The bag is a plain image, present from the first frame. -->
    <div class="h-6 w-6">
      <span
        v-if="adding"
        class="new-product-buy__spinner"
        aria-hidden="true"
      ></span>
      <img
        v-show="!adding"
        :src="shoppingBagImg"
        alt=""
        aria-hidden="true"
        width="24"
        height="24"
        class="h-full w-full object-contain"
      />
    </div>
  </button>
</template>
<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";
import shoppingBagImg from "~/assets/img/new-icons/shopping-bag.svg";
import { useProductAnalytics } from "~/core/components/templates/store/new-product/composables/useProductAnalytics";
import {
  addToCartKey,
  claimAddToCart,
  isAddToCartPending,
} from "~/core/utils/addToCartGuard";
import {
  confirmedAddedQuantity,
  snapshotCartAnalyticsProduct,
} from "~/core/utils/cartAnalytics";

const props = defineProps({
  product: {
    type: Object,
    default() {
      return null;
    },
  },
});
const store = useStore();
const { trackAddToCart } = useProductAnalytics();

// No :disabled while pending: a grey button reads as "out of stock". The
// pending state is shared with every other button showing this product
// (addToCartGuard.ts), so the main button and the sticky bar agree.
const adding = computed(() => isAddToCartPending(addToCartKey(props.product)));

const handleAddToCart = async () => {
  const product = props.product;
  // A second tap while an add for this product is in flight would add a
  // second unit and send a second add_to_cart event.
  const release = claimAddToCart(addToCartKey(product));
  if (!release) return;
  const quantity = 1;
  // Captured before the request: the sticky bar unmounts when the bag opens
  // and an SPA navigation replaces `props.product` while it is pending.
  const analyticsProduct = snapshotCartAnalyticsProduct(product);
  try {
    const result = await store.dispatch("store/cart/add", {
      product,
      quantity,
    });
    if (!analyticsProduct) return;
    const added = confirmedAddedQuantity(result, analyticsProduct.id, quantity);
    if (added > 0) trackAddToCart(analyticsProduct, added);
  } finally {
    release();
  }
};
</script>
<style lang="scss">
.new-product-buy__spinner {
  display: block;
  box-sizing: border-box;
  width: 75%;
  height: 75%;
  margin: 12.5%;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: new-product-buy-spin 0.8s linear infinite;
}

@keyframes new-product-buy-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .new-product-buy__spinner {
    animation-duration: 2.4s;
  }
}
</style>
